// @ts-nocheck
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useListPenaltyReviews } from '../../../services/loanService';
import type { PenaltyReview } from '../../../services/schemas/loanServiceSchemas/penaltyReview';
import { PenaltyReviewStatus } from '../../../services/schemas/loanServiceSchemas/penaltyReviewStatus';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import DataTable from 'react-data-table-component';
import { LoanAccountLabel } from '../../common/PendingChargesAlert';

interface GroupedPenalties {
  loanAccountId: string;
  totalPenalties: number;
  totalAmount: number;
  penalties: PenaltyReview[];
}

const PendingPenalties = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const ALL_PENALTIES_PAGE_SIZE = 10000;

  const { data: penaltiesData, isLoading } = useListPenaltyReviews({ status: PenaltyReviewStatus.PENDING, page: 0, size: ALL_PENALTIES_PAGE_SIZE });

  // Group penalties by loan account
  const groupedPenalties = React.useMemo(() => {
    if (!penaltiesData?.content) return [];

    const grouped = penaltiesData.content.reduce((acc: { [key: string]: GroupedPenalties }, review: PenaltyReview) => {
      const loanAccountId = review.accountId || '';
      if (!acc[loanAccountId]) {
        acc[loanAccountId] = {
          loanAccountId,
          totalPenalties: 0,
          totalAmount: 0,
          penalties: []
        };
      }

      acc[loanAccountId].totalPenalties++;
      acc[loanAccountId].totalAmount += review.calculated?.amountDec || 0;
      acc[loanAccountId].penalties.push(review);

      return acc;
    }, {});

    return Object.values(grouped);
  }, [penaltiesData?.content]);

  const columns = [
    {
      name: 'Loan Account',
      selector: (row: GroupedPenalties) => row.loanAccountId,
      sortable: true,
      cell: (row: GroupedPenalties) => <LoanAccountLabel accountId={row.loanAccountId} />,
    },
    {
      name: 'Pending Penalties',
      selector: (row: GroupedPenalties) => row.totalPenalties,
      sortable: true,
      cell: (row: GroupedPenalties) => (
        <span className="badge bg-warning text-dark">
          {row.totalPenalties} {row.totalPenalties === 1 ? 'Penalty' : 'Penalties'}
        </span>
      ),
    },
    {
      name: 'Total Amount',
      selector: (row: GroupedPenalties) => row.totalAmount,
      sortable: true,
      format: (row: GroupedPenalties) => formatCurrency(row.totalAmount),
    },
    {
      name: 'Actions',
      cell: (row: GroupedPenalties) => (
        <button
          className="btn btn-primary btn-sm"
          onClick={() => navigate(`/collections/penalties/${row.loanAccountId}`)}
        >
          <i className="fa fa-eye me-1"></i>
          View Details
        </button>
      ),
    },
  ];

  // Handle page change (DataTable is 1-based, API is 0-based)
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handlePerRowsChange = (newPerPage: number) => {
    setPageSize(newPerPage);
    setPage(1); // Reset to first page when changing page size
  };

  return (
    <div>
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <div className="container-fluid">
          <div className="row g-3 mb-3 align-items-center">
            <div className="col">
              <ol className="breadcrumb bg-transparent mb-0">
                <li className="breadcrumb-item"><a className="text-secondary" href="/">Home</a></li>
                <li className="breadcrumb-item"><a className="text-secondary" href="/collections">Collections</a></li>
                <li className="breadcrumb-item active" aria-current="page">Pending Charges</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          <div className="row g-3 mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Pending Charges</h5>
                </div>
                <div className="card-body">
                  {isLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading pending charges...</p>
                    </div>
                  ) : groupedPenalties.length === 0 ? (
                    <div className="text-center py-5">
                      <p className="text-muted">No pending charges found</p>
                    </div>
                  ) : (
                    <DataTable
                      columns={columns}
                      data={groupedPenalties}
                      pagination
                      onChangePage={handlePageChange}
                      onChangeRowsPerPage={handlePerRowsChange}
                      paginationPerPage={pageSize}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      highlightOnHover
                      striped
                      responsive
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingPenalties; 
